import React from "react";
import styled from "styled-components";
import { RouteComponentProps, withRouter } from "react-router";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { InputField } from "@sonder/ui/source/styled/fragments/InputField";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { PanelCard } from "@sonder/ui/source/styled/fragments/PanelCard";
import { PanelNav } from "@sonder/ui/source/styled/fragments/PanelNav";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { PanelContent } from "@sonder/ui/source/styled/layouts/PanelContent";
import { PanelHeader } from "@sonder/ui/source/styled/layouts/PanelHeader";
import { PanelLayout } from "@sonder/ui/source/styled/layouts/PanelLayout";
import { PanelNavContent } from "@sonder/ui/source/styled/layouts/PanelNavContent";
import { borders, colors, sizes } from "@sonder/ui/source/styled/theme";
import { useLoggedInUser } from "auth/hooks";
import { useStore as useAuthStore } from "auth/store";
import { useCreateTaskForm } from "tasks/hooks/useCreateTaskForm";

const Layout = styled.div`
  background-color: ${colors.grayF};

  box-shadow: ${colors.shadow};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  margin: 0rem auto;
  max-width: ${sizes.minBreakpoint};
  overflow: hidden;
  width: 100%;

  @media screen and (min-width: ${sizes.minBreakpoint}) {
    border: ${borders.standard};
    border-radius: 0.3rem;
    margin: 1rem auto;
    max-width: ${sizes.sidepanelWidth};
  }
`;

const StyledPanelHeader = styled(PanelHeader)`
  border-left: 0;
`;

const StyledPanelContent = styled(PanelContent)`
  background-color: ${colors.grayF};
  border-left: 0;

  ${PanelCard} {
    background-color: ${colors.grayF};
  }

  ${InputField} {
    background-color: transparent;
  }
`;

const Footer = styled.div`
  border-top: ${borders.standard};
  display: flex;
  grid-area: commentinput;
  justify-content: flex-end;
  padding: 1rem 1.71rem;
`;

const StandaloneCreateTaskForm = withRouter(
  ({ history }: RouteComponentProps) => {
    const {
      state: { user },
    } = useAuthStore();
    const {
      isBulkSubject,
      isCreateDisabled,
      isCreating,
      isSingleSubject,
      formState,
      onSubmit,
      setFormState,
      FormFields,
    } = useCreateTaskForm();
    const isOnlyCGSMember = user?.isGETMember && !user?.isTOMMember;
    return (
      <PanelLayout>
        <PanelNav>
          <PanelNavContent>
            <h2>Create Task</h2>
          </PanelNavContent>
        </PanelNav>
        <StyledPanelHeader>{FormFields.Header}</StyledPanelHeader>
        <StyledPanelContent>
          <PanelCard>{FormFields.Content}</PanelCard>
          {FormFields.Error && <PanelCard>{FormFields.Error}</PanelCard>}
        </StyledPanelContent>
        <Footer>
          <InputGroup>
            <Button
              disabled={isCreateDisabled}
              type="button"
              onClick={() =>
                setFormState({
                  ...formState,
                  isDispatching: !formState.isDispatching,
                })
              }
            >
              {formState.isDispatching ? "Back" : "Assign"}
            </Button>
            <PrimaryButton
              disabled={isCreateDisabled}
              type="submit"
              onClick={() =>
                onSubmit(taskOrJobId => {
                  // Standalone CreateTaskView is used by both CGS and non CGS users.
                  // For Bulk Subjects, CGS Reps don't have access to the Task List (/tasks), only standalone views.
                  // So, just redirect them back to Task Creation, not ideal, but an OK solution.
                  if (isBulkSubject)
                    history.push(isOnlyCGSMember ? `/task/create` : "/tasks");
                  // For Single Subjects, we can just redirect to the TaskID, CGS will have access to this.
                  if (isSingleSubject) history.push(`/task/${taskOrJobId}`);
                })
              }
            >
              {(isCreating && "Creating...") || "Create"}
            </PrimaryButton>
          </InputGroup>
        </Footer>
      </PanelLayout>
    );
  },
);

export const StandaloneCreateTaskView = () => {
  // Fetch user metadata and update auth stores
  useLoggedInUser();
  return (
    <Layout>
      <StandaloneCreateTaskForm />
    </Layout>
  );
};
